import React from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Link, useNavigate } from 'react-router-dom';
import { AtSign, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import {
  GET_MY_MENTIONS,
  MARK_MENTION_READ_MUTATION,
  MARK_ALL_MENTIONS_READ_MUTATION,
  GET_MY_UNREAD_MENTION_COUNT,
} from '../graphql/queries';

interface MentionRow {
  id: string;
  commentId: string;
  taskId: string;
  taskName: string;
  projectId: string;
  projectName: string;
  authorId: string;
  authorFirstName: string;
  authorLastName: string;
  contentExcerpt: string;
  readAt?: string | null;
  createdAt: string;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('pl-PL', {
    timeZone: 'Europe/Warsaw',
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

export const MentionsPage: React.FC = () => {
  const navigate = useNavigate();
  const { data, loading, refetch } = useQuery<{ myMentions: MentionRow[] }>(
    GET_MY_MENTIONS,
    { fetchPolicy: 'cache-and-network' },
  );

  const [markRead] = useMutation(MARK_MENTION_READ_MUTATION, {
    refetchQueries: [{ query: GET_MY_UNREAD_MENTION_COUNT }],
  });
  const [markAllRead, { loading: markingAll }] = useMutation(
    MARK_ALL_MENTIONS_READ_MUTATION,
    {
      refetchQueries: [{ query: GET_MY_UNREAD_MENTION_COUNT }],
    },
  );

  const mentions = data?.myMentions || [];

  const handleOpen = async (m: MentionRow) => {
    if (!m.readAt) {
      try {
        await markRead({ variables: { id: m.id } });
      } catch {
        /* non-blocking */
      }
    }
    navigate(`/projects/${m.projectId}/kanban?task=${m.taskId}`);
  };

  const handleMarkAll = async () => {
    try {
      await markAllRead();
      await refetch();
      toast.success('Wszystkie wzmianki oznaczono jako przeczytane');
    } catch (err: any) {
      toast.error(err?.message || 'Nie udało się zaktualizować');
    }
  };

  const hasUnread = mentions.some((m) => !m.readAt);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            <AtSign className="w-6 h-6" />
            Wzmianki
          </h2>
          <p className="text-sm text-muted-foreground">
            Komentarze, w których ktoś Cię wspomniał
          </p>
        </div>
        {hasUnread && (
          <Button
            type="button"
            variant="outline"
            onClick={handleMarkAll}
            disabled={markingAll}
          >
            <Check className="w-4 h-4 mr-1" />
            Oznacz wszystkie jako przeczytane
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {mentions.length} {mentions.length === 1 ? 'wzmianka' : 'wzmianek'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading && mentions.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">Wczytywanie…</p>
          ) : mentions.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">
              Brak wzmianek. Pojawią się tutaj, gdy ktoś użyje @TwojeImię w
              komentarzu do zadania.
            </p>
          ) : (
            <div className="space-y-2">
              {mentions.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => handleOpen(m)}
                  className={`block w-full text-left p-3 rounded-md border border-border hover:bg-accent/30 transition-colors ${
                    !m.readAt ? 'bg-primary/5 border-primary/30' : ''
                  }`}
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="flex items-baseline gap-2 min-w-0">
                      <span
                        className={`text-sm ${
                          !m.readAt ? 'font-semibold' : ''
                        }`}
                      >
                        {m.authorLastName} {m.authorFirstName}
                      </span>
                      <span className="text-xs text-muted-foreground truncate">
                        w {m.projectName} / {m.taskName}
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground flex-shrink-0">
                      {formatDate(m.createdAt)}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1 line-clamp-2 break-words">
                    {m.contentExcerpt}
                  </p>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
